import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { orderService } from '../services/orderService';
import { authService } from '../services/authService';
import Button from '../components/Button/Button';
import Input from '../components/Input/Input';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';

const DashboardWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background.secondary};
  padding-top: 80px;
  padding-bottom: 70px;
`;

const DashboardContainer = styled.div`
  display: flex;
  max-width: 1400px;
  margin: 0 auto;
  min-height: calc(100vh - 80px);
`;

const Sidebar = styled.div`
  width: 280px;
  background-color: ${props => props.theme.colors.background.primary};
  border-right: 1px solid ${props => props.theme.colors.border.light};
  padding: ${props => props.theme.spacing.xl};
  position: sticky;
  top: 80px;
  height: calc(100vh - 80px);
  overflow-y: auto;

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    display: ${props => props.mobileMenuOpen ? 'block' : 'none'};
    position: fixed;
    top: 80px;
    left: 0;
    width: 100%;
    height: calc(100vh - 80px);
    z-index: 999;
    border-right: none;
    border-bottom: 1px solid ${props => props.theme.colors.border.light};
  }
`;

const MobileMenuToggle = styled.button`
  display: none;
  position: fixed;
  top: 95px;
  left: 20px;
  z-index: 1000;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  font-size: 18px;
  cursor: pointer;
  box-shadow: ${props => props.theme.shadows.md};

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const UserProfile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${props => props.theme.spacing.xl} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryHover});
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => props.theme.fontSize.xl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: white;
  margin-bottom: ${props => props.theme.spacing.md};
  text-transform: uppercase;
`;

const UserName = styled.h2`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.xs};
  text-align: center;
`;

const UserEmail = styled.p`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  text-align: center;
`;

const NavigationMenu = styled.nav`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const NavItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border: none;
  background-color: ${props => props.active ? props.theme.colors.primaryLight : 'transparent'};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.text.primary};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  cursor: pointer;
  border-radius: ${props => props.theme.borderRadius.md};
  transition: all ${props => props.theme.transitions.fast};
  margin-bottom: ${props => props.theme.spacing.xs};
  text-align: left;

  &:hover {
    background-color: ${props => props.theme.colors.primaryLight};
    color: ${props => props.theme.colors.primary};
    transform: translateX(4px);
  }
`;

const NavIcon = styled.span`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const UserStats = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing.md};
`;

const StatCard = styled.div`
  background-color: ${props => props.theme.colors.background.secondary};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  text-align: center;
  border: 1px solid ${props => props.theme.colors.border.light};
`;

const StatNumber = styled.div`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const StatLabel = styled.div`
  font-size: ${props => props.theme.fontSize.xs};
  color: ${props => props.theme.colors.text.secondary};
`;

const MainContent = styled.div`
  flex: 1;
  padding: ${props => props.theme.spacing.xl};
  overflow-y: auto;
`;

const ContentHeader = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const ContentTitle = styled.h1`
  font-size: ${props => props.theme.fontSize.xxl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const ContentSubtitle = styled.p`
  font-size: ${props => props.theme.fontSize.base};
  color: ${props => props.theme.colors.text.secondary};
`;

const Card = styled.div`
  background-color: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border.light};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
`;

const CardTitle = styled.h2`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
`;

const ProfileForm = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.lg};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const FormRow = styled.div`
  grid-column: 1 / -1;
`;

const ProfileDetail = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const DetailLabel = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-weight: ${props => props.theme.fontWeight.medium};
`;

const DetailValue = styled.span`
  font-size: ${props => props.theme.fontSize.base};
  color: ${props => props.theme.colors.text.primary};
  font-weight: ${props => props.theme.fontWeight.medium};
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const OrderCard = styled.div`
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    border-color: ${props => props.theme.colors.primary}40;
    box-shadow: ${props => props.theme.shadows.md};
    transform: translateY(-2px);
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.background.secondary};
  border-bottom: 1px solid ${props => props.theme.colors.border.light};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.md};
    align-items: flex-start;
  }
`;

const OrderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const OrderNumber = styled.span`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
`;

const OrderDate = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const OrderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${props => props.theme.spacing.sm};
  }
`;

const OrderStatus = styled.span`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.semibold};
  background-color: ${props => {
    switch (props.status) {
      case 'completed':
        return props.theme.colors.success + '15';
      case 'processing':
        return props.theme.colors.info + '15';
      case 'pending':
        return props.theme.colors.warning + '15';
      case 'cancelled':
        return props.theme.colors.error + '15';
      default:
        return props.theme.colors.background.tertiary;
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed':
        return props.theme.colors.success;
      case 'processing':
        return props.theme.colors.info;
      case 'pending':
        return props.theme.colors.warning;
      case 'cancelled':
        return props.theme.colors.error;
      default:
        return props.theme.colors.text.secondary;
    }
  }};
`;

const OrderTotal = styled.span`
  font-size: ${props => props.theme.fontSize.xl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
`;

const OrderActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.background.primary};
  border-top: 1px solid ${props => props.theme.colors.border.light};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const BillModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.lg};
`;

const BillModal = styled.div`
  background-color: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.lg};
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const BillModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.xl};
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  position: sticky;
  top: 0;
  background-color: ${props => props.theme.colors.background.primary};
  z-index: 1;
`;

const BillModalTitle = styled.h2`
  font-size: ${props => props.theme.fontSize.xl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${props => props.theme.colors.text.secondary};
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background-color: ${props => props.theme.colors.background.secondary};
    color: ${props => props.theme.colors.text.primary};
  }
`;

const BillContent = styled.div`
  padding: ${props => props.theme.spacing.xl};
`;

const BillHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxxl};
  padding-bottom: ${props => props.theme.spacing.xl};
  border-bottom: 2px solid ${props => props.theme.colors.border.medium};
`;

const CompanyName = styled.h1`
  font-size: ${props => props.theme.fontSize.xxxl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const BillTitle = styled.h2`
  font-size: ${props => props.theme.fontSize.xl};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const BillInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xxxl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const BillSection = styled.div``;

const SectionTitle = styled.h3`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  padding-bottom: ${props => props.theme.spacing.sm};
`;

const InfoLine = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSize.sm};
`;

const InfoLabel = styled.span`
  color: ${props => props.theme.colors.text.secondary};
  font-weight: ${props => props.theme.fontWeight.medium};
`;

const InfoValue = styled.span`
  color: ${props => props.theme.colors.text.primary};
  font-weight: ${props => props.theme.fontWeight.medium};
`;

const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const TableHeader = styled.thead`
  background-color: ${props => props.theme.colors.background.secondary};
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: ${props => props.theme.colors.background.secondary}50;
  }
`;

const TableHeaderCell = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  border-bottom: 2px solid ${props => props.theme.colors.border.medium};
`;

const TableCell = styled.td`
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  color: ${props => props.theme.colors.text.primary};
`;

const TotalSection = styled.div`
  border-top: 2px solid ${props => props.theme.colors.border.medium};
  padding-top: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.lg};
`;

const TotalLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.sm};
  font-size: ${props => props.fontSize || props.theme.fontSize.base};
  font-weight: ${props => props.fontWeight || props.theme.fontWeight.medium};
  color: ${props => props.color || props.theme.colors.text.primary};
`;

const BillActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: center;
  margin-top: ${props => props.theme.spacing.xl};
  padding-top: ${props => props.theme.spacing.xl};
  border-top: 1px solid ${props => props.theme.colors.border.light};
`;

const DownloadsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const DownloadCard = styled.div`
  background-color: ${props => props.theme.colors.background.secondary};
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    border-color: ${props => props.theme.colors.primary}40;
    box-shadow: ${props => props.theme.shadows.sm};
    transform: translateY(-2px);
  }
`;

const DownloadHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${props => props.theme.spacing.md};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.sm};
  }
`;

const DownloadInfo = styled.div`
  flex: 1;
`;

const DownloadName = styled.h4`
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const DownloadMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const DownloadDetail = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const ProductName = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.fontWeight.medium};
`;

const DownloadActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  align-items: flex-end;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const DownloadStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.fontSize.xs};
  color: ${props => props.available ? props.theme.colors.success : props.theme.colors.warning};
  font-weight: ${props => props.theme.fontWeight.medium};

  &::before {
    content: ${props => props.available ? "'●'" : "'⏳'"};
    font-size: 8px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xxxl};
  color: ${props => props.theme.colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: ${props => props.theme.fontSize.xxxl};
  margin-bottom: ${props => props.theme.spacing.lg};
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  font-size: ${props => props.theme.fontSize.xl};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const EmptyText = styled.p`
  font-size: ${props => props.theme.fontSize.base};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.text.secondary};
  gap: ${props => props.theme.spacing.sm};
`;

const ErrorMessage = styled.div`
  background-color: ${props => props.theme.colors.error}08;
  border: 1px solid ${props => props.theme.colors.error}20;
  color: ${props => props.theme.colors.error};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const DashboardPage = () => {
  const { user, updateUser, isLoading: authLoading, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [orders, setOrders] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadsLoading, setDownloadsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showBillModal, setShowBillModal] = useState(false);
  const [billLoading, setBillLoading] = useState(false);
  const [dataInitialized, setDataInitialized] = useState(false);
  const [userStats, setUserStats] = useState({
    totalOrders: 0,
    totalSpent: 0,
    completedOrders: 0,
    downloadsAvailable: 0,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      company: user?.company || '',
      address1: user?.address1 || '',
      address2: user?.address2 || '',
      city: user?.city || '',
      state: user?.state || '',
      postcode: user?.postcode || '',
      country: user?.country || '',
    },
  });

  const fetchDownloads = async () => {
    if (!user?.id) return;

    try {
      setDownloadsLoading(true);
      const userDownloads = await orderService.getAllCustomerDownloads(user.id);
      console.log("Fetched downloads:", userDownloads);
      setDownloads(userDownloads);
      setUserStats((prev) => ({
        ...prev,
        downloadsAvailable: userDownloads.length,
      }));
    } catch (err) {
      console.error("Error fetching downloads:", err);
    } finally {
      setDownloadsLoading(false);
    }
  };

  useEffect(() => {
    const initializeDashboard = async () => {
      if (authLoading) {
        console.log('Auth still loading...');
        return;
      }

      if (!isAuthenticated || !user?.id) {
        console.log('Not authenticated or no user ID');
        setDataInitialized(true);
        return;
      }

      if (!dataInitialized) {
        console.log('Initializing dashboard data for user:', user.id);
        await fetchDashboardData();
        setDataInitialized(true);
      }
    };

    initializeDashboard();
  }, [authLoading, isAuthenticated, user?.id, dataInitialized]);

  useEffect(() => {
    if (activeTab === 'downloads' && dataInitialized && user?.id) {
      fetchDownloads();
    }
  }, [activeTab, dataInitialized, user?.id]);

  useEffect(() => {
    if (user && dataInitialized) {
      reset({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        company: user.company || '',
        address1: user.address1 || '',
        address2: user.address2 || '',
        city: user.city || '',
        state: user.state || '',
        postcode: user.postcode || '',
        country: user.country || '',
      });
    }
  }, [user, dataInitialized, reset]);

  const fetchDashboardData = async () => {
    if (!user?.id) {
      console.log('No user ID available for fetching data');
      return;
    }
    
    try {
      console.log('Fetching dashboard data for user:', user.id);
      setLoading(true);
      setError(null);
      
      const ordersResponse = await orderService.getOrders(user.id, { per_page: 20 });
      console.log('Orders response:', ordersResponse);
      
      setOrders(ordersResponse.orders || []);
      
      const stats = {
        totalOrders: ordersResponse.total || 0,
        totalSpent: (ordersResponse.orders || []).reduce((sum, order) => sum + parseFloat(order.total || 0), 0),
        completedOrders: (ordersResponse.orders || []).filter(order => order.status === 'completed').length,
        downloadsAvailable: 0,
      };
      
      console.log('Calculated stats:', stats);
      setUserStats(stats);
    } catch (err) {
      console.error('Error fetching order details:', err);
      setError('Failed to load order details');
    } finally {
      setBillLoading(false);
    }
  };

  const generatePDF = () => {
    if (!selectedOrder) return;

    const printWindow = window.open('', '_blank');
    const billHTML = generateBillHTML(selectedOrder);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice #${selectedOrder.number}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 20px; 
              color: #333; 
            }
            .bill-container { max-width: 800px; margin: 0 auto; }
            .bill-header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #ccc; padding-bottom: 20px; }
            .company-name { font-size: 28px; font-weight: bold; color: #2563eb; margin-bottom: 10px; }
            .bill-title { font-size: 20px; margin-bottom: 20px; }
            .bill-info { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-bottom: 40px; }
            .section-title { font-size: 16px; font-weight: bold; margin-bottom: 10px; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
            .info-line { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 14px; }
            .info-label { color: #666; font-weight: 500; }
            .info-value { color: #333; font-weight: 500; }
            .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            .table-header { background-color: #f8f9fa; }
            .table-row:nth-child(even) { background-color: #f8f9fa; }
            .table-header-cell { padding: 12px; text-align: left; font-weight: bold; border-bottom: 2px solid #ccc; }
            .table-cell { padding: 12px; border-bottom: 1px solid #eee; }
            .total-section { border-top: 2px solid #ccc; padding-top: 20px; margin-top: 20px; }
            .total-line { display: flex; justify-content: space-between; margin-bottom: 8px; }
            .total-line.grand-total { font-size: 18px; font-weight: bold; margin-top: 12px; padding-top: 12px; border-top: 1px solid #ccc; }
            @media print { 
              body { margin: 0; } 
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${billHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const generateBillHTML = (order) => {
    const formatCurrency = (amount) => `${parseFloat(amount).toFixed(2)}`;
    
    return `
      <div class="bill-container">
        <div class="bill-header">
          <div class="company-name">Techy Garry</div>
          <div class="bill-title">INVOICE</div>
          <div style="color: #666;">Invoice #${order.number}</div>
        </div>

        <div class="bill-info">
          <div>
            <div class="section-title">Bill To:</div>
            <div>${order.billing.first_name} ${order.billing.last_name}</div>
            <div>${order.billing.address_1}</div>
            ${order.billing.address_2 ? `<div>${order.billing.address_2}</div>` : ''}
            <div>${order.billing.city}, ${order.billing.state} ${order.billing.postcode}</div>
            <div>${order.billing.country}</div>
            <div style="margin-top: 10px;">Email: ${order.billing.email}</div>
            ${order.billing.phone ? `<div>Phone: ${order.billing.phone}</div>` : ''}
          </div>

          <div>
            <div class="section-title">Order Details:</div>
            <div class="info-line">
              <span class="info-label">Order Number:</span>
              <span class="info-value">#${order.number}</span>
            </div>
            <div class="info-line">
              <span class="info-label">Order Date:</span>
              <span class="info-value">${new Date(order.date_created).toLocaleDateString()}</span>
            </div>
            <div class="info-line">
              <span class="info-label">Status:</span>
              <span class="info-value">${orderService.formatOrderStatus(order.status)}</span>
            </div>
            <div class="info-line">
              <span class="info-label">Payment Method:</span>
              <span class="info-value">${order.payment_method_title || order.payment_method}</span>
            </div>
          </div>
        </div>

        <table class="items-table">
          <thead class="table-header">
            <tr>
              <th class="table-header-cell">Product</th>
              <th class="table-header-cell" style="text-align: center;">Quantity</th>
              <th class="table-header-cell" style="text-align: right;">Unit Price</th>
              <th class="table-header-cell" style="text-align: right;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${order.line_items.map(item => `
              <tr class="table-row">
                <td class="table-cell">${item.name}</td>
                <td class="table-cell" style="text-align: center;">${item.quantity}</td>
                <td class="table-cell" style="text-align: right;">${formatCurrency(item.price)}</td>
                <td class="table-cell" style="text-align: right;">${formatCurrency(item.total)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="total-section">
          <div class="total-line">
            <span>Subtotal:</span>
            <span>${formatCurrency(order.total - order.total_tax)}</span>
          </div>
          ${parseFloat(order.shipping_total) > 0 ? `
            <div class="total-line">
              <span>Shipping:</span>
              <span>${formatCurrency(order.shipping_total)}</span>
            </div>
          ` : ''}
          ${parseFloat(order.total_tax) > 0 ? `
            <div class="total-line">
              <span>Tax:</span>
              <span>${formatCurrency(order.total_tax)}</span>
            </div>
          ` : ''}
          ${parseFloat(order.discount_total) > 0 ? `
            <div class="total-line">
              <span>Discount:</span>
              <span>-${formatCurrency(order.discount_total)}</span>
            </div>
          ` : ''}
          <div class="total-line grand-total">
            <span>Total:</span>
            <span>${formatCurrency(order.total)}</span>
          </div>
        </div>

        <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; color: #666; font-size: 12px; text-align: center;">
          <p>Thank you for your business!</p>
          <p>If you have any questions about this invoice, please contact us.</p>
        </div>
      </div>
    `;
  };

  const onSubmitProfileUpdate = async (data) => {
    try {
      const updatedUser = await authService.updateUserProfile(data);
      updateUser(updatedUser);
      setIsEditingProfile(false);
      reset(updatedUser);
    } catch (err) {
      setError(err.message);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatCurrency = (amount) => `${parseFloat(amount || 0).toFixed(2)}`;

  const isDownloadAvailable = (download) => {
    if (download.access_expires && download.access_expires !== 'never') {
      const isExpired = orderService.isDownloadExpired(download.access_expires);
      if (isExpired) return false;
    }
    
    if (download.downloads_remaining) {
      if (download.downloads_remaining === 'unlimited' || download.downloads_remaining === 'never') {
        return true;
      }
      
      const remaining = parseInt(download.downloads_remaining);
      if (!isNaN(remaining)) {
        return remaining > 0;
      }
    }
    
    return true;
  };

  const getDownloadStatusText = (download) => {
    if (download.access_expires && download.access_expires !== 'never') {
      if (orderService.isDownloadExpired(download.access_expires)) {
        return 'Expired';
      }
    }
    
    if (download.downloads_remaining) {
      if (download.downloads_remaining === 'unlimited' || download.downloads_remaining === 'never') {
        return 'Available';
      }
      
      const remaining = parseInt(download.downloads_remaining);
      if (!isNaN(remaining)) {
        if (remaining === 0) {
          return 'No downloads left';
        }
        return `${remaining} left`;
      }
    }
    
    return 'Available';
  };

  const handleDownload = async (download) => {
    const downloadId = download.id || `${download.product_id}-${download.order_id}`;
    
    try {
      setDownloadingIds(prev => new Set([...prev, downloadId]));
      
      const downloadUrl = download.download_url || orderService.getSecureDownloadUrl(
        download.file, 
        download.order_id, 
        download.product_id
      );
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = orderService.formatDownloadFileName(download.name);
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => {
        setDownloadingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(downloadId);
          return newSet;
        });
      }, 2000);
      
    } catch (err) {
      console.error('Download failed:', err);
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(downloadId);
        return newSet;
      });
    }
  };

  const navigationItems = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'profile', label: 'Profile', icon: '👤' },
    { id: 'orders', label: 'Orders', icon: '📦' },
    { id: 'downloads', label: 'Downloads', icon: '⬇️' },
  ];

  if (authLoading || (isAuthenticated && !dataInitialized)) {
    return <LoadingSpinner fullHeight />;
  }

  if (!isAuthenticated) {
    return (
      <DashboardWrapper>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <h2>Please sign in to access your dashboard</h2>
          <Button as={Link} to="/signin">Sign In</Button>
        </div>
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper>
      <MobileMenuToggle onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
        {mobileMenuOpen ? '×' : '☰'}
      </MobileMenuToggle>
      
      <DashboardContainer>
        <Sidebar mobileMenuOpen={mobileMenuOpen}>
          <UserProfile>
            <Avatar>
              {getInitials(user?.displayName || `${user?.firstName} ${user?.lastName}`)}
            </Avatar>
            <UserName>
              {user?.displayName || `${user?.firstName} ${user?.lastName}` || 'User'}
            </UserName>
            <UserEmail>{user?.email}</UserEmail>
          </UserProfile>

          <NavigationMenu>
            {navigationItems.map((item) => (
              <NavItem
                key={item.id}
                active={activeTab === item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setMobileMenuOpen(false);
                }}
              >
                <NavIcon>{item.icon}</NavIcon>
                {item.label}
              </NavItem>
            ))}
          </NavigationMenu>

          <UserStats>
            <StatCard>
              <StatNumber>{userStats.totalOrders}</StatNumber>
              <StatLabel>Total Orders</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{formatCurrency(userStats.totalSpent)}</StatNumber>
              <StatLabel>Total Spent</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{userStats.completedOrders}</StatNumber>
              <StatLabel>Completed</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{userStats.downloadsAvailable}</StatNumber>
              <StatLabel>Downloads</StatLabel>
            </StatCard>
          </UserStats>
        </Sidebar>

        <MainContent>
          <ContentHeader>
            <ContentTitle>
              {activeTab === 'overview' && 'Dashboard Overview'}
              {activeTab === 'profile' && 'Profile Settings'}
              {activeTab === 'orders' && 'Order History'}
              {activeTab === 'downloads' && 'Your Downloads'}
            </ContentTitle>
            <ContentSubtitle>
              {activeTab === 'overview' && 'Welcome back! Here\'s your account overview.'}
              {activeTab === 'profile' && 'Manage your personal information and preferences.'}
              {activeTab === 'orders' && 'View and track all your orders.'}
              {activeTab === 'downloads' && 'Access your digital products and downloads.'}
            </ContentSubtitle>
          </ContentHeader>

          {activeTab === 'overview' && (
            <Card>
              <CardHeader>
                <CardTitle>Account Summary</CardTitle>
              </CardHeader>
              <ProfileDetail>
                <DetailItem>
                  <DetailLabel>Full Name</DetailLabel>
                  <DetailValue>
                    {user?.displayName || `${user?.firstName} ${user?.lastName}` || 'Not set'}
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Email Address</DetailLabel>
                  <DetailValue>{user?.email || 'Not set'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Phone Number</DetailLabel>
                  <DetailValue>{user?.phone || 'Not set'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Company</DetailLabel>
                  <DetailValue>{user?.company || 'Not set'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Recent Orders</DetailLabel>
                  <DetailValue>{userStats.totalOrders} total orders</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Available Downloads</DetailLabel>
                  <DetailValue>{userStats.downloadsAvailable} files</DetailValue>
                </DetailItem>
              </ProfileDetail>
            </Card>
          )}

          {activeTab === 'profile' && (
            <Card>
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditingProfile(!isEditingProfile)}
                >
                  {isEditingProfile ? 'Cancel' : 'Edit Profile'}
                </Button>
              </CardHeader>

              {isEditingProfile ? (
                <ProfileForm onSubmit={handleSubmit(onSubmitProfileUpdate)}>
                  <Input
                    label="First Name"
                    error={errors.firstName?.message}
                    {...register('firstName', {
                      required: 'First name is required',
                    })}
                  />
                  <Input
                    label="Last Name"
                    error={errors.lastName?.message}
                    {...register('lastName', {
                      required: 'Last name is required',
                    })}
                  />
                  <FormRow>
                    <Input
                      label="Email Address"
                      type="email"
                      error={errors.email?.message}
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: 'Please enter a valid email',
                        },
                      })}
                    />
                  </FormRow>
                  <Input
                    label="Phone Number"
                    type="tel"
                    {...register('phone')}
                  />
                  <Input
                    label="Company"
                    {...register('company')}
                  />
                  <FormRow>
                    <Input
                      label="Address Line 1"
                      {...register('address1')}
                    />
                  </FormRow>
                  <FormRow>
                    <Input
                      label="Address Line 2"
                      {...register('address2')}
                    />
                  </FormRow>
                  <Input
                    label="City"
                    {...register('city')}
                  />
                  <Input
                    label="State/Province"
                    {...register('state')}
                  />
                  <Input
                    label="Postal Code"
                    {...register('postcode')}
                  />
                  <Input
                    label="Country"
                    {...register('country')}
                  />
                  <FormRow>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? <LoadingSpinner size="20px" showText={false} /> : 'Save Changes'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsEditingProfile(false);
                          reset();
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </FormRow>
                </ProfileForm>
              ) : (
                <ProfileDetail>
                  <DetailItem>
                    <DetailLabel>First Name</DetailLabel>
                    <DetailValue>{user?.firstName || 'Not set'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Last Name</DetailLabel>
                    <DetailValue>{user?.lastName || 'Not set'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Email Address</DetailLabel>
                    <DetailValue>{user?.email || 'Not set'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Phone Number</DetailLabel>
                    <DetailValue>{user?.phone || 'Not set'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Company</DetailLabel>
                    <DetailValue>{user?.company || 'Not set'}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Address</DetailLabel>
                    <DetailValue>
                      {user?.address1 ? (
                        <>
                          {user.address1}
                          {user.address2 && <><br />{user.address2}</>}
                          {(user.city || user.state || user.postcode) && <br />}
                          {user.city && `${user.city}, `}
                          {user.state && `${user.state} `}
                          {user.postcode}
                          {user.country && <><br />{user.country}</>}
                        </>
                      ) : (
                        'Not set'
                      )}
                    </DetailValue>
                  </DetailItem>
                </ProfileDetail>
              )}
            </Card>
          )}

          {activeTab === 'orders' && (
            <Card>
              <CardHeader>
                <CardTitle>Order History</CardTitle>
                <Button as={Link} to="/shop" variant="outline" size="sm">
                  New Order
                </Button>
              </CardHeader>

              {error && <ErrorMessage>Failed to load orders: {error}</ErrorMessage>}

              {orders.length > 0 ? (
                <OrdersList>
                  {orders.map((order) => (
                    <OrderCard key={order.id}>
                      <OrderHeader>
                        <OrderInfo>
                          <OrderNumber>Order #{order.number}</OrderNumber>
                          <OrderDate>
                            {orderService.formatOrderDate(order.date_created)}
                          </OrderDate>
                        </OrderInfo>
                        <OrderMeta>
                          <OrderStatus status={order.status}>
                            {orderService.formatOrderStatus(order.status)}
                          </OrderStatus>
                          <OrderTotal>{formatCurrency(order.total)}</OrderTotal>
                        </OrderMeta>
                      </OrderHeader>
                      <OrderActions>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewBill(order)}
                        >
                          View Bill
                        </Button>
                        {order.status === 'completed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewBill(order)}
                          >
                            Download Invoice
                          </Button>
                        )}
                      </OrderActions>
                    </OrderCard>
                  ))}
                </OrdersList>
              ) : (
                <EmptyState>
                  <EmptyIcon>📦</EmptyIcon>
                  <EmptyTitle>No orders yet</EmptyTitle>
                  <EmptyText>
                    Start shopping to see your orders here
                  </EmptyText>
                  <Button as={Link} to="/shop">
                    Browse Products
                  </Button>
                </EmptyState>
              )}
            </Card>
          )}

          {activeTab === 'downloads' && (
            <Card>
              <CardHeader>
                <CardTitle>Your Downloads</CardTitle>
                <Button onClick={fetchDownloads} variant="outline" size="sm">
                  Refresh
                </Button>
              </CardHeader>

              {downloadsLoading ? (
                <LoadingContainer>
                  <LoadingSpinner size="20px" showText={false} />
                  Loading your downloads...
                </LoadingContainer>
              ) : downloads.length > 0 ? (
                <DownloadsContainer>
                  {downloads.map((download, index) => {
                    const downloadId = download.id || `${download.product_id}-${download.order_id}`;
                    const isDownloading = downloadingIds.has(downloadId);
                    const available = isDownloadAvailable(download);
                    
                    return (
                      <DownloadCard key={downloadId}>
                        <DownloadHeader>
                          <DownloadInfo>
                            <DownloadName>{download.name}</DownloadName>
                            <DownloadMeta>
                              <ProductName>From: {download.product_name}</ProductName>
                              <DownloadDetail>
                                Order: #{download.order_id}
                              </DownloadDetail>
                              {download.access_expires && download.access_expires !== 'never' && (
                                <DownloadDetail>
                                  Expires: {new Date(download.access_expires).toLocaleDateString()}
                                </DownloadDetail>
                              )}
                              {download.access_expires === 'never' && (
                                <DownloadDetail>
                                  Access: Never expires
                                </DownloadDetail>
                              )}
                              <DownloadDetail>
                                Downloads: {download.downloads_remaining === 'unlimited' ? 'Unlimited' : download.downloads_remaining}
                              </DownloadDetail>
                            </DownloadMeta>
                          </DownloadInfo>
                          
                          <DownloadActions>
                            <DownloadStatus available={available}>
                              {getDownloadStatusText(download)}
                            </DownloadStatus>
                            
                            <Button
                              size="sm"
                              disabled={!available || isDownloading}
                              onClick={() => handleDownload(download)}
                            >
                              {isDownloading ? (
                                <>
                                  <LoadingSpinner size="14px" showText={false} />
                                  <span style={{ marginLeft: '0.5rem' }}>Downloading...</span>
                                </>
                              ) : (
                                'Download'
                              )}
                            </Button>
                          </DownloadActions>
                        </DownloadHeader>
                      </DownloadCard>
                    );
                  })}
                </DownloadsContainer>
              ) : (
                <EmptyState>
                  <EmptyIcon>📥</EmptyIcon>
                  <EmptyTitle>No downloads available</EmptyTitle>
                  <EmptyText>
                    Purchase digital products to access downloads here. All your downloadable 
                    files will appear in this section once your orders are completed.
                  </EmptyText>
                  <Button as={Link} to="/shop">
                    Browse Digital Products
                  </Button>
                </EmptyState>
              )}
            </Card>
          )}
        </MainContent>
      </DashboardContainer>

      {showBillModal && (
        <BillModalOverlay onClick={() => setShowBillModal(false)}>
          <BillModal onClick={(e) => e.stopPropagation()}>
            <BillModalHeader>
              <BillModalTitle>
                {selectedOrder ? `Invoice #${selectedOrder.number}` : 'Loading...'}
              </BillModalTitle>
              <CloseButton onClick={() => setShowBillModal(false)}>
                ×
              </CloseButton>
            </BillModalHeader>

            <BillContent>
              {billLoading ? (
                <LoadingContainer>
                  <LoadingSpinner size="20px" showText={false} />
                  Loading invoice details...
                </LoadingContainer>
              ) : selectedOrder ? (
                <>
                  <BillHeader>
                    <CompanyName>Techy Garry</CompanyName>
                    <BillTitle>INVOICE</BillTitle>
                    <div style={{ color: '#666' }}>Invoice #{selectedOrder.number}</div>
                  </BillHeader>

                  <BillInfo>
                    <BillSection>
                      <SectionTitle>Bill To:</SectionTitle>
                      <div style={{ marginBottom: '8px' }}>
                        {selectedOrder.billing.first_name} {selectedOrder.billing.last_name}
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        {selectedOrder.billing.address_1}
                      </div>
                      {selectedOrder.billing.address_2 && (
                        <div style={{ marginBottom: '8px' }}>
                          {selectedOrder.billing.address_2}
                        </div>
                      )}
                      <div style={{ marginBottom: '8px' }}>
                        {selectedOrder.billing.city}, {selectedOrder.billing.state} {selectedOrder.billing.postcode}
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        {selectedOrder.billing.country}
                      </div>
                      <div style={{ marginTop: '12px', marginBottom: '8px' }}>
                        Email: {selectedOrder.billing.email}
                      </div>
                      {selectedOrder.billing.phone && (
                        <div>Phone: {selectedOrder.billing.phone}</div>
                      )}
                    </BillSection>

                    <BillSection>
                      <SectionTitle>Order Details:</SectionTitle>
                      <InfoLine>
                        <InfoLabel>Order Number:</InfoLabel>
                        <InfoValue>#{selectedOrder.number}</InfoValue>
                      </InfoLine>
                      <InfoLine>
                        <InfoLabel>Order Date:</InfoLabel>
                        <InfoValue>
                          {new Date(selectedOrder.date_created).toLocaleDateString()}
                        </InfoValue>
                      </InfoLine>
                      <InfoLine>
                        <InfoLabel>Status:</InfoLabel>
                        <InfoValue>{orderService.formatOrderStatus(selectedOrder.status)}</InfoValue>
                      </InfoLine>
                      <InfoLine>
                        <InfoLabel>Payment Method:</InfoLabel>
                        <InfoValue>
                          {selectedOrder.payment_method_title || selectedOrder.payment_method}
                        </InfoValue>
                      </InfoLine>
                    </BillSection>
                  </BillInfo>

                  <ItemsTable>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell>Product</TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'center' }}>Quantity</TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'right' }}>Unit Price</TableHeaderCell>
                        <TableHeaderCell style={{ textAlign: 'right' }}>Total</TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <tbody>
                      {selectedOrder.line_items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell style={{ textAlign: 'center' }}>{item.quantity}</TableCell>
                          <TableCell style={{ textAlign: 'right' }}>
                            {formatCurrency(item.price)}
                          </TableCell>
                          <TableCell style={{ textAlign: 'right' }}>
                            {formatCurrency(item.total)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </tbody>
                  </ItemsTable>

                  <TotalSection>
                    <TotalLine>
                      <span>Subtotal:</span>
                      <span>{formatCurrency(selectedOrder.total - selectedOrder.total_tax)}</span>
                    </TotalLine>
                    {parseFloat(selectedOrder.shipping_total) > 0 && (
                      <TotalLine>
                        <span>Shipping:</span>
                        <span>{formatCurrency(selectedOrder.shipping_total)}</span>
                      </TotalLine>
                    )}
                    {parseFloat(selectedOrder.total_tax) > 0 && (
                      <TotalLine>
                        <span>Tax:</span>
                        <span>{formatCurrency(selectedOrder.total_tax)}</span>
                      </TotalLine>
                    )}
                    {parseFloat(selectedOrder.discount_total) > 0 && (
                      <TotalLine>
                        <span>Discount:</span>
                        <span>-{formatCurrency(selectedOrder.discount_total)}</span>
                      </TotalLine>
                    )}
                    <TotalLine 
                      fontSize="18px" 
                      fontWeight="bold" 
                      style={{ 
                        marginTop: '12px', 
                        paddingTop: '12px', 
                        borderTop: '1px solid #ccc' 
                      }}
                    >
                      <span>Total:</span>
                      <span>{formatCurrency(selectedOrder.total)}</span>
                    </TotalLine>
                  </TotalSection>

                  <BillActions>
                    <Button variant="outline" onClick={() => setShowBillModal(false)}>
                      Close
                    </Button>
                    <Button onClick={generatePDF}>
                      Download PDF
                    </Button>
                  </BillActions>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ color: '#666' }}>Failed to load invoice details</div>
                </div>
              )}
            </BillContent>
          </BillModal>
        </BillModalOverlay>
      )}
    </DashboardWrapper>
  );
};

export default DashboardPage;